// Admin AI Assistant — MVP MOCK ONLY.
//
// SECURITY / ARCHITECTURE TODO:
// This module performs NO internet search and uses NO AI API and NO API keys.
// It returns local, clearly-labeled placeholder suggestions so the admin UI can
// be built and tested. Later, replace the body of fetchAiSuggestions() with a
// call to a serverless endpoint, e.g.:
//
//   const token = await auth.currentUser.getIdToken();
//   const res = await fetch("/api/ai-suggestions", {
//     method: "POST",
//     headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
//     body: JSON.stringify(params),
//   });
//   return res.json();
//
// API keys and the real web-search + AI extraction MUST live ONLY in that backend
// function (which must also verify the caller is an admin). Never in this frontend.

import { EVENT_STATUS } from "../config/eventOptions";
import { parseAtomsSuggestions } from "../utils/atomsImport";

const SAMPLE_TITLES = {
  news: ["scene update", "new venue opening", "festival lineup announced", "label showcase"],
  review: ["club night review", "festival recap", "listening session", "gig review"],
  event_idea: ["rooftop session idea", "warehouse concept night", "open-air picnic set", "gallery + DJ night"],
};

const SAMPLE_MOODS = ["Party", "Chill", "Creative", "Social"];

export const confidenceLabel = (value) => {
  if (typeof value !== "number") return "—";
  if (value >= 0.8) return "High";
  if (value >= 0.5) return "Medium";
  return "Low";
};

// Mock generator. Deterministic (no Math.random / no Date.now in the data shape)
// so results are predictable while the backend is not connected yet.
export const fetchAiSuggestions = async ({
  text = "",
  city = "",
  genres = [],
  startDate = "",
  endDate = "",
  sourceType = "news",
} = {}) => {
  // Simulate a short backend round-trip.
  await new Promise((resolve) => setTimeout(resolve, 400));

  // If the admin pasted valid Atoms JSON, use it directly — no mock filler.
  const atoms = parseAtomsSuggestions(text);
  if (atoms) return atoms;

  const kind = SAMPLE_TITLES[sourceType] ? sourceType : "news";
  const titles = SAMPLE_TITLES[kind];
  const baseGenres = genres.length ? genres : ["Electronic"];
  const where = city || "your city";
  const range = startDate || endDate ? ` (${startDate || "…"} – ${endDate || "…"})` : "";

  return titles.map((label, index) => {
    const primaryGenre = baseGenres[index % baseGenres.length];
    return {
      title: `${primaryGenre} ${label} in ${where}`,
      city,
      genres: baseGenres.slice(0, 2),
      moods: [SAMPLE_MOODS[index % SAMPLE_MOODS.length]],
      description: `Mock ${kind.replace("_", " ")} suggestion for ${where}${range}. Replace with real AI/search results from the backend later.`,
      sourceUrl: "",
      imageUrl: "",
      confidence: 0.5 + (index % 3) * 0.15,
      notes: "Mock suggestion — backend AI/search not yet connected. Review before approving.",
    };
  });
};

// Convert an accepted suggestion into a Firestore draft document. The caller
// adds the server timestamp at write time. "Event idea" suggestions are saved
// as NEWS drafts, tagged via contentKind + a notes prefix (Events import logic
// is intentionally untouched).
export const buildDraftFromSuggestion = (suggestion, { sourceType, adminUid }) => {
  const isEventIdea = sourceType === "event_idea";
  const data = {
    title: suggestion.title || "",
    city: suggestion.city || "",
    genres: suggestion.genres || [],
    moods: suggestion.moods || [],
    description: suggestion.description || "",
    imageUrl: suggestion.imageUrl || "",
    sourceUrl: suggestion.sourceUrl || "",
    confidence: typeof suggestion.confidence === "number" ? suggestion.confidence : null,
    notes: isEventIdea ? `[Event idea] ${suggestion.notes || ""}`.trim() : suggestion.notes || "",
    contentKind: sourceType, // "news" | "review" | "event_idea"
    status: EVENT_STATUS.DRAFT,
    source: "ai_suggestion",
    suggestedBy: "ai",
    creatorId: adminUid,
    publishedAt: new Date().toISOString(),
  };
  if (suggestion.country) data.country = suggestion.country;
  if (typeof suggestion.score === "number") data.score = suggestion.score;
  if (suggestion.scoreReason) data.scoreReason = suggestion.scoreReason;
  if (suggestion.priorityRegion) data.priorityRegion = suggestion.priorityRegion;
  return data;
};
